import { lazy, Suspense, useMemo, useRef, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeInfo, Upload } from 'lucide-react';
import { Card, CardContent } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { Separator } from '@ui/separator.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { toast } from 'sonner';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useUser } from '@r/api/auth.tsx';
import {
  adminWorkspaceConfigs,
  apiAdminworkspaceConfigsIdPatch,
  apiAdminworkspaceConfigsPost,
  apiMediasIdGet,
  apiMediasPost
} from '@r/api/generated/client.ts';
import type {
  ConfigConfigReadModel,
  WorkspaceConfigWorkspaceConfigReadModel
} from '@r/api/generated/client.schemas.ts';
import { getConfigsFromResponse, useAllConfigs } from '@r/hooks/use-all-configs.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
// Chargé à la demande : sort la lib de recadrage du chunk de la page.
const ImageCropperDialog = lazy(() =>
  import('@r/components/import/image-cropper-dialog.tsx').then((module) => ({
    default: module.ImageCropperDialog
  }))
);

/** Fichier choisi par l'admin, pas encore envoyé au serveur (évite les uploads S3 abandonnés). */
type PendingMedia = {
  file: File;
};

type ImageDisplayInfo = {
  name: string;
  dimensions?: string;
  size?: string;
};

type ResolvedMediaInfo = {
  pathUri?: string;
  name?: string;
  sizeBytes?: number;
};

type MediaConfigSection = {
  titleKey: string;
  items: ConfigConfigReadModel[];
};

const ACCEPTED_MIME_TYPES = ['image/png', 'image/jpg', 'image/jpeg', 'image/svg+xml'];
const LEGAL_MEDIA_KEYS = new Set(['regulation', 'privacy-policy', 'legal-notice']);
const EXCLUDED_MEDIA_CONFIG_TITLES = new Set(['config.media.email.color.bg.title']);

// Extrait un ID numérique depuis une IRI media de type /medias/{id}.
function extractMediaId(mediaIri?: string | null): number | null {
  if (!mediaIri) return null;
  const mediaMatch = mediaIri.match(/\/medias\/(\d+)$/);
  if (!mediaMatch?.[1]) return null;
  const mediaId = Number.parseInt(mediaMatch[1], 10);
  return Number.isFinite(mediaId) ? mediaId : null;
}

// Vérifie si une valeur peut être utilisée directement comme URL d'image.
function isImageUrl(value?: string | null): boolean {
  if (!value) return false;
  return value.startsWith('http://') || value.startsWith('https://') || value.startsWith('blob:');
}

// Normalise le type MIME pour gérer les fichiers sans type explicite.
function normalizeFileType(file: File): string {
  if (file.type) {
    return file.type.toLowerCase();
  }
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'jpg') return 'image/jpg';
  if (extension === 'jpeg') return 'image/jpeg';
  if (extension === 'png') return 'image/png';
  if (extension === 'svg') return 'image/svg+xml';
  return '';
}

// Formate une taille en octets dans un format lisible (o, ko, Mo).
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} ko`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} Mo`;
}

// Récupère le nom de fichier depuis un chemin/URL.
function getFilenameFromPath(path: string): string {
  const cleanPath = path.split('?')[0] ?? path;
  const fromPath = cleanPath.split('/').pop() || cleanPath;
  return decodeURIComponent(fromPath);
}

// Lit les dimensions intrinsèques d'une image via son URL.
function readImageDimensions(url: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

// URL blob éphémère : révoquée dès la lecture des dimensions (aperçu géré par `useObjectUrlForFile`).
async function readImageDimensionsFromFile(
  file: File
): Promise<{ width: number; height: number } | null> {
  const tempUrl = URL.createObjectURL(file);
  try {
    return await readImageDimensions(tempUrl);
  } finally {
    URL.revokeObjectURL(tempUrl);
  }
}

/** Blob URL d'un fichier local, révoquée au changement de fichier ou au démontage. */
function useObjectUrlForFile(file: File | undefined): string | undefined {
  const [objectUrl, setObjectUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!file) {
      setObjectUrl(undefined);
      return;
    }

    const nextUrl = URL.createObjectURL(file);
    setObjectUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [file]);

  return objectUrl;
}

type AssetRowPreviewImageProps = {
  pendingFile?: File;
  remoteUrl?: string;
  alt: string;
  className: string;
};

function AssetRowPreviewImage({
  pendingFile,
  remoteUrl,
  alt,
  className
}: AssetRowPreviewImageProps) {
  const pendingPreviewUrl = useObjectUrlForFile(pendingFile);
  const src = pendingPreviewUrl ?? remoteUrl;

  if (!src) {
    return null;
  }

  return <img src={src} alt={alt} className={className} />;
}

// Définit un ordre d'affichage prioritaire pour certains médias.
function getOrderByKey(key: string): number {
  const order: Record<string, number> = {
    logo: 1,
    favicon: 2
  };
  return order[key] ?? 99;
}

// Vérifie que toutes les sous-chaînes attendues sont présentes.
function includesAll(value: string, parts: string[]): boolean {
  const normalized = value.toLowerCase();
  return parts.every((part) => normalized.includes(part));
}

// Trouve la première config qui correspond à des motifs sur key/title.
function findConfigByPatterns(
  configs: ConfigConfigReadModel[],
  patterns: { keyParts?: string[]; titleParts?: string[] }[]
): ConfigConfigReadModel | undefined {
  return configs.find((config) => {
    return patterns.some((pattern) => {
      const keyMatch = pattern.keyParts ? includesAll(config.key, pattern.keyParts) : false;
      const titleMatch = pattern.titleParts ? includesAll(config.title, pattern.titleParts) : false;
      return keyMatch || titleMatch;
    });
  });
}

// Retourne la clé i18n de label à afficher pour une config média.
function getDirectLabelKey(config: ConfigConfigReadModel): string {
  const key = config.key.toLowerCase();
  const title = config.title.toLowerCase();

  if (key === 'logo' || title.includes('logo.title')) {
    return 'personalization.assets.label.logo';
  }
  if (key === 'favicon' || title.includes('favicon.title')) {
    return 'personalization.assets.label.favicon';
  }
  if (includesAll(key, ['email', 'logo']) || includesAll(title, ['email', 'logo'])) {
    return 'personalization.assets.label.email.logo';
  }
  if (includesAll(key, ['public', 'bg']) || includesAll(title, ['public', 'bg'])) {
    return 'personalization.assets.label.auth.background';
  }
  if (includesAll(key, ['program', 'slider']) || includesAll(title, ['program', 'slider'])) {
    return 'personalization.assets.label.home.illustration';
  }
  if (includesAll(key, ['program', 'hero']) || includesAll(title, ['program', 'hero'])) {
    return 'personalization.assets.label.program.illustration';
  }
  if (
    includesAll(key, ['shop', 'home', 'cover']) ||
    includesAll(title, ['shop', 'home', 'cover'])
  ) {
    return 'personalization.assets.label.shop.home.cover';
  }

  return config.title || config.key;
}

// Retourne la clé i18n de tooltip associée à une config média.
function getDirectInfoKey(config: ConfigConfigReadModel): string | undefined {
  const key = config.key.toLowerCase();
  const title = config.title.toLowerCase();

  if (key === 'logo' || title.includes('logo.title')) {
    return 'personalization.assets.info.logo';
  }
  if (key === 'favicon' || title.includes('favicon.title')) {
    return 'personalization.assets.info.favicon';
  }
  if (includesAll(key, ['email', 'logo']) || includesAll(title, ['email', 'logo'])) {
    return 'personalization.assets.info.email.logo';
  }
  if (includesAll(key, ['public', 'bg']) || includesAll(title, ['public', 'bg'])) {
    return 'personalization.assets.info.auth.background';
  }
  if (includesAll(key, ['program', 'slider']) || includesAll(title, ['program', 'slider'])) {
    return 'personalization.assets.info.home.illustration';
  }
  if (includesAll(key, ['program', 'hero']) || includesAll(title, ['program', 'hero'])) {
    return 'personalization.assets.info.program.illustration';
  }

  return undefined;
}

// Construit les sections d'affichage et leur ordre métier.
function buildSections(configs: ConfigConfigReadModel[]): MediaConfigSection[] {
  const filteredConfigs = configs.filter(
    (config) =>
      config.type === 'media' &&
      !LEGAL_MEDIA_KEYS.has(config.key) &&
      !EXCLUDED_MEDIA_CONFIG_TITLES.has(config.title)
  );

  const logoAndFavicon = filteredConfigs
    .filter((config) => config.key === 'logo' || config.key === 'favicon')
    .sort((a, b) => getOrderByKey(a.key) - getOrderByKey(b.key));

  const emailLogos = filteredConfigs.filter(
    (config) =>
      (includesAll(config.key, ['mail']) || includesAll(config.key, ['email'])) &&
      !includesAll(config.key, ['color', 'bg'])
  );

  const authBackground = findConfigByPatterns(filteredConfigs, [
    { keyParts: ['public', 'bg'] },
    { titleParts: ['public', 'bg'] }
  ]);
  const homeIllustration = findConfigByPatterns(filteredConfigs, [
    { keyParts: ['program', 'slider'] },
    { titleParts: ['program', 'slider'] }
  ]);
  const programIllustration = findConfigByPatterns(filteredConfigs, [
    { keyParts: ['program', 'hero'] },
    { titleParts: ['program', 'hero'] }
  ]);

  const programImages = [authBackground, homeIllustration, programIllustration].filter(
    (config): config is ConfigConfigReadModel => Boolean(config)
  );

  return [
    { titleKey: 'personalization.assets.section.program.logo', items: logoAndFavicon },
    { titleKey: 'personalization.assets.section.email.logo', items: emailLogos },
    { titleKey: 'personalization.assets.section.program.images', items: programImages }
  ].filter((section) => section.items.length > 0);
}

// Page d'administration des assets: aperçu, upload et validation des images.
export function PersonalizationAssetsPage() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const user = useUser();
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Une entrée par config média : active le bouton « Valider » tant qu'un fichier est en attente.
  const [pendingMediaByConfigId, setPendingMediaByConfigId] = useState<
    Record<number, PendingMedia>
  >({});
  const [imageInfoByConfigId, setImageInfoByConfigId] = useState<Record<number, ImageDisplayInfo>>(
    {}
  );
  const [submittingConfigId, setSubmittingConfigId] = useState<number | null>(null);
  // Fichier logo en attente de recadrage (avant `onSelectImage`).
  const [logoCropTarget, setLogoCropTarget] = useState<{
    config: ConfigConfigReadModel;
    file: File;
  } | null>(null);
  const hydratedInfoConfigIdsRef = useRef<Set<number> | undefined>(undefined);
  hydratedInfoConfigIdsRef.current ??= new Set();

  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const roles = activeWorkspace?.roles ?? [];
  const canManageAssets = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_FIDDY_ADMIN');

  const allConfigsQuery = useAllConfigs();
  const mediaConfigs = useMemo(
    () => buildSections(getConfigsFromResponse(allConfigsQuery.data) ?? []),
    [allConfigsQuery.data]
  );

  const workspaceConfigsQuery = useQuery<WorkspaceConfigWorkspaceConfigReadModel[]>({
    queryKey: ['workspaceConfigs', 'personalization-assets'],
    queryFn: async () => {
      const response = await adminWorkspaceConfigs({ 'config.type': 'media' });
      return (response.data || []) as WorkspaceConfigWorkspaceConfigReadModel[];
    },
    enabled: canManageAssets
  });

  const mediaIdsToResolve = useMemo(() => {
    const ids = new Set<number>();
    for (const workspaceConfig of workspaceConfigsQuery.data ?? []) {
      const mediaId = extractMediaId(workspaceConfig.media);
      if (mediaId) {
        ids.add(mediaId);
      }
    }
    return [...ids];
  }, [workspaceConfigsQuery.data]);

  const mediasInfoByIdQuery = useQuery<Record<number, ResolvedMediaInfo>>({
    queryKey: [
      'workspaceConfigs',
      'personalization-assets-medias-info',
      mediaIdsToResolve.join(',')
    ],
    enabled: canManageAssets && mediaIdsToResolve.length > 0,
    queryFn: async () => {
      const mediaEntries = await Promise.all(
        mediaIdsToResolve.map(async (mediaId) => {
          try {
            const media = await apiMediasIdGet(String(mediaId));
            const mediaUnknown = media as unknown as Record<string, unknown>;

            const possibleNameKeys = ['originalName', 'name', 'filename', 'fileName'];
            const possibleSizeKeys = ['size', 'fileSize', 'contentLength', 'weight'];

            const name = possibleNameKeys.find((key) => typeof mediaUnknown[key] === 'string');
            const size = possibleSizeKeys.find((key) => typeof mediaUnknown[key] === 'number');

            return [
              mediaId,
              {
                pathUri: media.pathUri || undefined,
                name: (name ? (mediaUnknown[name] as string) : undefined) || undefined,
                sizeBytes: (size ? (mediaUnknown[size] as number) : undefined) || undefined
              }
            ] as const;
          } catch {
            return [mediaId, {}] as const;
          }
        })
      );
      return Object.fromEntries(mediaEntries);
    }
  });

  const workspaceConfigsByConfigId = useMemo(() => {
    const byConfigId = new Map<number, WorkspaceConfigWorkspaceConfigReadModel>();
    for (const workspaceConfig of workspaceConfigsQuery.data ?? []) {
      const configId = workspaceConfig.config?.id;
      if (configId) {
        byConfigId.set(configId, workspaceConfig);
      }
    }
    return byConfigId;
  }, [workspaceConfigsQuery.data]);

  // Récupère la workspaceConfig associée à une config donnée.
  const getWorkspaceConfigFor = (configId?: number) => {
    if (!configId) return undefined;
    return workspaceConfigsByConfigId.get(configId);
  };

  // Hydrate les métadonnées d'images existantes (nom, dimensions, taille si disponible).
  useEffect(() => {
    let cancelled = false;

    const hydrateExistingImagesInfo = async () => {
      type HydrateTask = {
        configId: number;
        pathUri: string;
        name: string;
        size?: string;
      };

      const tasks: HydrateTask[] = [];

      for (const section of mediaConfigs) {
        for (const config of section.items) {
          if (!config.id) continue;
          if (pendingMediaByConfigId[config.id]) continue;
          if (hydratedInfoConfigIdsRef.current?.has(config.id)) continue;

          const workspaceConfig = workspaceConfigsByConfigId.get(config.id);
          const mediaId = extractMediaId(workspaceConfig?.media);
          const groupedConfigValue = workspaceConfigContext?.groupedConfigs?.media?.[config.key] as
            | string
            | undefined;
          const mediaInfo = mediaId ? mediasInfoByIdQuery.data?.[mediaId] : undefined;
          const pathUri =
            mediaInfo?.pathUri ?? (isImageUrl(groupedConfigValue) ? groupedConfigValue : undefined);
          if (!pathUri) continue;

          tasks.push({
            configId: config.id,
            pathUri,
            name: mediaInfo?.name || getFilenameFromPath(pathUri),
            size: mediaInfo?.sizeBytes ? formatSize(mediaInfo.sizeBytes) : undefined
          });
        }
      }

      if (tasks.length === 0) return;

      const hydratedEntries = await Promise.all(
        tasks.map(async (task) => {
          const dimensions = await readImageDimensions(task.pathUri);
          return [
            task.configId,
            {
              name: task.name,
              dimensions: dimensions ? `${dimensions.width}*${dimensions.height}px` : undefined,
              size: task.size
            }
          ] as const;
        })
      );

      if (cancelled) return;

      for (const [configId] of hydratedEntries) {
        hydratedInfoConfigIdsRef.current?.add(configId);
      }

      setImageInfoByConfigId((previous) => ({
        ...previous,
        ...Object.fromEntries(hydratedEntries)
      }));
    };

    void hydrateExistingImagesInfo();

    return () => {
      cancelled = true;
    };
  }, [
    mediaConfigs,
    mediasInfoByIdQuery.data,
    pendingMediaByConfigId,
    workspaceConfigContext?.groupedConfigs?.media,
    workspaceConfigsByConfigId
  ]);

  /**
   * Étape 1 du flux image : validation du format, aperçu via blob URL et métadonnées UI.
   * Aucun appel API ici — l'upload est reporté à `onSubmitImage` pour limiter le spam S3.
   */
  const onSelectImage = async (config: ConfigConfigReadModel, file: File) => {
    const fileType = normalizeFileType(file);
    if (!ACCEPTED_MIME_TYPES.includes(fileType)) {
      toast.error(t('personalization.assets.toast.invalid.format'));
      return;
    }

    const dimensions = await readImageDimensionsFromFile(file);

    setPendingMediaByConfigId((previous) => ({
      ...previous,
      [config.id ?? 0]: { file }
    }));
    const configId = config.id ?? null;
    if (configId !== null) {
      setImageInfoByConfigId((previous) => ({
        ...previous,
        [configId]: {
          name: file.name,
          dimensions: dimensions ? `${dimensions.width}*${dimensions.height}px` : undefined,
          size: formatSize(file.size)
        }
      }));
    }

    toast.success(t('personalization.assets.toast.media.selected', { id: file.name }));
  };

  /**
   * Étape 2 du flux image : upload du fichier en attente (`POST /medias`), puis liaison
   * à la workspace config (patch ou création). En cas d'échec, le pending reste pour réessayer.
   */
  const onSubmitImage = async (config: ConfigConfigReadModel) => {
    const configId = config.id;
    if (!configId) return;

    const pendingMedia = pendingMediaByConfigId[configId];
    if (!pendingMedia) {
      toast.error(t('personalization.assets.toast.select.before.validate'));
      return;
    }

    setSubmittingConfigId(configId);
    try {
      // Envoi S3 + enregistrement média côté API (déclenché uniquement à la validation).
      const media = await apiMediasPost({
        file: pendingMedia.file,
        type: 'assets'
      });

      if (!media.id || !media.iri) {
        toast.error(t('toast.error.label'));
        return;
      }

      const existingWorkspaceConfig = getWorkspaceConfigFor(configId);
      if (existingWorkspaceConfig?.id) {
        await apiAdminworkspaceConfigsIdPatch(existingWorkspaceConfig.id.toString(), {
          value: String(media.id),
          media: media.iri
        });
      } else {
        await apiAdminworkspaceConfigsPost({
          value: String(media.id),
          media: media.iri,
          config: `/configs/${configId}`
        });
      }

      toast.success(t('personalization.assets.toast.saved'));
      setPendingMediaByConfigId((previous) => {
        const updated = { ...previous };
        delete updated[configId];
        return updated;
      });

      await queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] });
      await workspaceConfigsQuery.refetch();
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'post', '/medias')) {
        showOrvalErrorToast(error, t('toast.error.label'));
        return;
      }
      if (isOrvalErrorFromPath(error, 'patch', '/admin/workspaceConfigs')) {
        showOrvalErrorToast(error, t('toast.error.label'));
        return;
      }
      if (isOrvalErrorFromPath(error, 'post', '/admin/workspaceConfigs')) {
        showOrvalErrorToast(error, t('toast.error.label'));
        return;
      }
      toast.error(t('toast.error.label'));
    } finally {
      setSubmittingConfigId(null);
    }
  };

  if (!canManageAssets) {
    return (
      <div className="space-y-2">
        <h1 className="text-4xl font-bold">{t('personalization.assets.title')}</h1>
        <p className="text-muted-foreground">{t('personalization.assets.access.denied')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1 className="text-4xl font-bold">{t('personalization.assets.title')}</h1>
      </div>

      <Card className="w-full">
        <CardContent className="p-0">
          {mediaConfigs.map((section, sectionIndex) => (
            <div key={section.titleKey} className="p-6">
              <h2 className="text-lg font-semibold">{t(section.titleKey)}</h2>
              <Separator className="mt-3 mb-4" />

              <div className="space-y-2">
                {section.items.map((config, itemIndex) => {
                  const pending = pendingMediaByConfigId[config.id ?? 0];
                  const existingWorkspaceConfig = getWorkspaceConfigFor(config.id);
                  const translatedConfigLabel = t(getDirectLabelKey(config));
                  const infoKey = getDirectInfoKey(config);
                  const isTopBrandAsset =
                    config.key === 'logo' ||
                    config.key === 'favicon' ||
                    includesAll(config.key, ['email', 'logo']) ||
                    includesAll(config.key, ['mail', 'logo']);
                  const groupedConfigValue = workspaceConfigContext?.groupedConfigs?.media?.[
                    config.key
                  ] as string | undefined;
                  const existingMediaIri = existingWorkspaceConfig?.media;
                  const existingMediaId = extractMediaId(existingMediaIri);
                  const existingPathUri =
                    (existingMediaId
                      ? mediasInfoByIdQuery.data?.[existingMediaId]?.pathUri
                      : undefined) || undefined;

                  const fallbackGroupedPreview = isImageUrl(groupedConfigValue)
                    ? groupedConfigValue
                    : undefined;
                  const remotePreviewUrl = existingPathUri ?? fallbackGroupedPreview;
                  const imageInfo = config.id ? imageInfoByConfigId[config.id] : undefined;
                  const hasPreview = Boolean(pending?.file || remotePreviewUrl);

                  return (
                    <div key={config.key}>
                      <div className="flex gap-4 justify-between items-center py-3">
                        <div className="flex gap-1 items-center w-[260px] text-sm text-muted-foreground">
                          <span>{translatedConfigLabel}</span>
                          {infoKey && (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex justify-center items-center text-muted-foreground/70 hover:text-foreground"
                                    aria-label={t('personalization.assets.info.icon.aria.label')}
                                  >
                                    <BadgeInfo className="w-4 h-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  {t(infoKey)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>

                        <div className="flex flex-1 gap-4 items-center">
                          {hasPreview ? (
                            <div className="flex gap-3 items-center">
                              <div className="flex justify-center items-center w-20 h-12 rounded border bg-muted/20">
                                <AssetRowPreviewImage
                                  pendingFile={pending?.file}
                                  remoteUrl={pending?.file ? undefined : remotePreviewUrl}
                                  alt={translatedConfigLabel}
                                  className={
                                    isTopBrandAsset
                                      ? 'object-contain max-w-full max-h-full rounded'
                                      : 'object-cover w-full h-full rounded'
                                  }
                                />
                              </div>
                              {imageInfo && (
                                <span className="text-xs text-muted-foreground">
                                  {imageInfo.name}
                                  {imageInfo.dimensions ? ` ${imageInfo.dimensions}` : ''}
                                  {imageInfo.size ? ` ${imageInfo.size}` : ''}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              {t('personalization.assets.no.image')}
                            </span>
                          )}
                        </div>

                        <div className="flex gap-2 items-center">
                          <input
                            ref={(element) => {
                              fileInputRefs.current[config.key] = element;
                            }}
                            type="file"
                            className="hidden"
                            accept=".png,.jpg,.jpeg,.svg,image/png,image/jpg,image/jpeg,image/svg+xml"
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (file) {
                                // Le logo passe par le recadrage (un SVG pourra être
                                // uploadé tel quel depuis la modale).
                                if (config.key === 'logo') {
                                  setLogoCropTarget({ config, file });
                                } else {
                                  void onSelectImage(config, file);
                                }
                              }
                              event.target.value = '';
                            }}
                          />

                          <Button
                            size="icon"
                            variant="ghost"
                            type="button"
                            onClick={() => fileInputRefs.current[config.key]?.click()}
                          >
                            <Upload className="w-4 h-4" />
                          </Button>

                          <Button
                            rounded="full"
                            size="custom_sm"
                            type="button"
                            loading={submittingConfigId === config.id}
                            disabled={!pending}
                            onClick={() => onSubmitImage(config)}
                          >
                            {t('personalization.assets.validate')}
                          </Button>
                        </div>
                      </div>

                      {itemIndex < section.items.length - 1 && <Separator />}
                    </div>
                  );
                })}
              </div>

              {sectionIndex < mediaConfigs.length - 1 && <Separator className="mt-6" />}
            </div>
          ))}
        </CardContent>
      </Card>

      {logoCropTarget ? (
        <Suspense fallback={null}>
          <ImageCropperDialog
            file={logoCropTarget.file}
            aspect={[
              { label: t('crop.shape.rectangle'), value: 3 },
              { label: t('crop.shape.square'), value: 1 }
            ]}
            onConfirm={(croppedFile) => {
              const target = logoCropTarget;
              setLogoCropTarget(null);
              void onSelectImage(target.config, croppedFile);
            }}
            onUploadOriginal={(originalFile) => {
              const target = logoCropTarget;
              setLogoCropTarget(null);
              void onSelectImage(target.config, originalFile);
            }}
            onCancel={() => setLogoCropTarget(null)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
